/**
 * Sountsou 2026 - Testimonials Slider
 * Carrousel de témoignages léger, accessible et fluide
 */

export function initTestimonialsSlider(root = document) {
    const sliders = root.querySelectorAll('.sountsou-testimonials');

    sliders.forEach((sliderEl) => {
        // Évite la double initialisation sur le même élément
        if (sliderEl.dataset.sliderInitialized === 'true') {
            return;
        }
        sliderEl.dataset.sliderInitialized = 'true';

        const stage = sliderEl.querySelector('.sountsou-testimonials__stage');
        const slides = Array.from(sliderEl.querySelectorAll('.sountsou-testimonials__slide'));
        const prevBtn = sliderEl.querySelector('.sountsou-testimonials__arrow--prev');
        const nextBtn = sliderEl.querySelector('.sountsou-testimonials__arrow--next');

        if (slides.length <= 1) {
            // Un seul témoignage : pas besoin de carrousel interactif
            if (prevBtn) prevBtn.style.display = 'none';
            if (nextBtn) nextBtn.style.display = 'none';
            return;
        }

        // Configuration
        const isAutoplayConfigured = sliderEl.dataset.autoplay !== '0' && sliderEl.dataset.autoplay !== 'false';
        const speedSeconds = parseInt(sliderEl.dataset.autoplaySpeed, 10) || 6;
        const intervalMs = Math.max(2000, Math.min(30000, speedSeconds * 1000));

        // Détecte les préférences d'accessibilité (Mouvement réduit)
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Détecte si le script s'exécute dans l'éditeur Gutenberg
        const isInEditor = Boolean(
            (typeof window.acf !== 'undefined' && document.body.classList.contains('wp-admin')) ||
            sliderEl.closest('.block-editor-block-list__layout') ||
            sliderEl.closest('.editor-styles-wrapper')
        );

        let currentIndex = slides.findIndex((s) => s.classList.contains('is-active'));
        if (currentIndex === -1) {
            currentIndex = 0;
            slides[0].classList.add('is-active');
            slides[0].setAttribute('aria-hidden', 'false');
        }

        let autoplayTimer = null;
        let isHovered = false;
        let isFocused = false;

        // Changement de diapositive
        function goToSlide(targetIndex) {
            if (targetIndex < 0) {
                targetIndex = slides.length - 1;
            } else if (targetIndex >= slides.length) {
                targetIndex = 0;
            }

            if (targetIndex === currentIndex) {
                return;
            }

            // Diapositive précédente : fade out
            slides[currentIndex].classList.remove('is-active');
            slides[currentIndex].setAttribute('aria-hidden', 'true');

            // Nouvelle diapositive : fade in
            currentIndex = targetIndex;
            slides[currentIndex].classList.add('is-active');
            slides[currentIndex].setAttribute('aria-hidden', 'false');

            // Relancer le timer d'autoplay pour ne pas changer trop vite après un clic
            restartAutoplay();
        }

        function nextSlide() {
            goToSlide(currentIndex + 1);
        }

        function prevSlide() {
            goToSlide(currentIndex - 1);
        }

        // Gestion de l'Autoplay
        function startAutoplay() {
            if (!isAutoplayConfigured || prefersReducedMotion || isInEditor) {
                return;
            }
            if (autoplayTimer) {
                clearInterval(autoplayTimer);
            }
            autoplayTimer = setInterval(() => {
                if (!isHovered && !isFocused) {
                    nextSlide();
                }
            }, intervalMs);
        }

        function stopAutoplay() {
            if (autoplayTimer) {
                clearInterval(autoplayTimer);
                autoplayTimer = null;
            }
        }

        function restartAutoplay() {
            stopAutoplay();
            startAutoplay();
        }

        // Événements boutons Précédent / Suivant
        if (prevBtn) {
            prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                prevSlide();
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                nextSlide();
            });
        }

        // Pause au survol de la souris
        sliderEl.addEventListener('mouseenter', () => {
            isHovered = true;
            stopAutoplay();
        });

        sliderEl.addEventListener('mouseleave', () => {
            isHovered = false;
            startAutoplay();
        });

        // Pause au focus clavier (Accessibilité WCAG 2.2.2)
        sliderEl.addEventListener('focusin', () => {
            isFocused = true;
            stopAutoplay();
        });

        sliderEl.addEventListener('focusout', () => {
            isFocused = false;
            startAutoplay();
        });

        // Navigation au clavier (Flèches gauche et droite)
        sliderEl.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                prevSlide();
            } else if (e.key === 'ArrowRight') {
                nextSlide();
            }
        });

        // Support du swipe tactile sur mobile
        if (stage) {
            let touchStartX = 0;
            let touchStartY = 0;

            stage.addEventListener('touchstart', (e) => {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
            }, { passive: true });

            stage.addEventListener('touchend', (e) => {
                const touchEndX = e.changedTouches[0].clientX;
                const touchEndY = e.changedTouches[0].clientY;
                const diffX = touchEndX - touchStartX;
                const diffY = touchEndY - touchStartY;

                // Si le geste est principalement horizontal et d'une amplitude > 45px
                if (Math.abs(diffX) > 45 && Math.abs(diffX) > Math.abs(diffY)) {
                    if (diffX > 0) {
                        prevSlide();
                    } else {
                        nextSlide();
                    }
                }
            }, { passive: true });
        }

        // Démarrer l'autoplay si applicable
        startAutoplay();
    });
}

// Hook de rafraîchissement dynamique pour Gutenberg (ACF Block Preview)
if (typeof window !== 'undefined' && typeof window.acf !== 'undefined') {
    window.acf.addAction('render_block_preview/type=testimonials-slider', function ($el) {
        if ($el && $el[0]) {
            initTestimonialsSlider($el[0]);
        }
    });
}
